import { BaseApiService } from './base';
import { authManager } from '../authManager';
import API_CONFIG, { API_ENDPOINTS } from '../../config/api';
import { buildApiUrl } from '../../utils/apiUrl';

export class AuthService extends BaseApiService {
  private normalizeUser(data: any) {
    const roles = Array.isArray(data.roles)
      ? data.roles.map((role: any) => role.code || role)
      : [];

    // Le rôle principal : priorité au champ role du modèle User, puis au premier rôle de la liste
    const primaryRole = data.role || roles[0] || 'creator';
    
    // Log pour debug
    console.log('🔍 normalizeUser - Debug:', {
      email: data.email,
      dataRole: data.role,
      rolesArray: roles,
      primaryRole: primaryRole,
    });

    return {
      id: data.id,
      email: data.email,
      username: data.username,
      first_name: data.first_name || '',
      last_name: data.last_name || '',
      full_name: data.full_name || `${data.first_name || ''} ${data.last_name || ''}`.trim(),
      role: primaryRole, // Utiliser le champ role du modèle User en priorité
      roles: roles.length > 0 ? roles : (data.role ? [data.role] : []), // Garder les deux pour compatibilité
      capabilities: Array.isArray(data.capabilities) ? data.capabilities : [],
      department: data.department_name || data.department || '',
      department_id: data.department_id ?? null,
      position: data.position || '',
      avatar_url: data.avatar_url || null,
      is_active: data.is_active !== undefined ? data.is_active : true,
    };
  }

  async login(email: string, password: string): Promise<{ user: any; tokens: any }> {
    console.log('🔐 Auth Service - Connexion API réelle pour:', email);
    
    try {
      // Créer un AbortController pour gérer le timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // Timeout de 10 secondes
      
      // Appel API réel au backend Django avec timeout
      // BEST PRACTICE: Utiliser buildApiUrl() pour construire l'URL de manière sûre
      const loginUrl = buildApiUrl(API_ENDPOINTS.LOGIN);
      console.log('🔗 URL de connexion complète:', loginUrl);
      const response = await fetch(loginUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email, 
          password 
        }),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || 'Erreur de connexion');
      }

      const data = await response.json();
      console.log('✅ Réponse backend reçue:', data);
      
      // Le backend retourne: { access, refresh, user, message }
      const tokens = {
        access: data.access,
        refresh: data.refresh
      };
      
      const user = this.normalizeUser(data.user);

      // Le jeton est enregistré avant tout autre appel. Il l'était après, si
      // bien que le chargement des habilitations partait sans authentification
      // et prenait un 401 ; l'erreur était avalée par le catch, et l'agent se
      // retrouvait avec zéro habilitation. Tout ce qui en dépend — les
      // activités, les équipes, les paramètres — lui restait alors fermé sans
      // qu'aucun message ne le dise.
      authManager.setTokens(tokens.access, tokens.refresh);

      // Si les capabilities ne sont pas présentes, les charger depuis l'endpoint /permissions/me/
      if (!user.capabilities || user.capabilities.length === 0) {
        try {
          console.log('📋 Chargement des capabilities depuis /permissions/me/...');
          const { settingsService } = await import('./settings');
          const permissionsData = await settingsService.getMyPermissions();
          if (permissionsData?.capabilities) {
            user.capabilities = permissionsData.capabilities;
            console.log('✅ Capabilities chargées:', user.capabilities.length);
          }
        } catch (error) {
          console.warn('⚠️ Impossible de charger les capabilities, utilisation des données de login');
        }
      }

      authManager.setUser(user);

      console.log('✅ Auth Service - Connexion réussie');
      console.log('👤 Utilisateur:', user.full_name);
      console.log('🔑 Access Token stocké');
      console.log('🔐 Capabilities:', user.capabilities?.length || 0);

      return { user, tokens };
    } catch (error: any) {
      console.error('❌ Erreur de connexion:', error.message);
      
      // Gérer les erreurs de timeout
      if (error.name === 'AbortError' || error.message.includes('aborted')) {
        console.error('⏱️ Timeout de connexion - Le serveur met trop de temps à répondre');
        throw new Error('Le serveur met trop de temps à répondre. Veuillez réessayer.');
      }
      
      // NE PAS faire de fallback sur les erreurs d'authentification (401, 403)
      // Ces erreurs sont LÉGITIMES et doivent être affichées à l'utilisateur
      
      // Seulement fallback si le serveur est vraiment inaccessible (erreur réseau)
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        console.warn('⚠️ Backend non disponible');
        throw new Error('Impossible de contacter le serveur. Vérifiez votre connexion.');
      }
      
      // Pour toutes les autres erreurs (401, 403, etc.), les propager
      throw error;
    }
  }

  async logout(): Promise<{ success: boolean }> {
    console.log('🔓 Auth Service - Déconnexion');
    
    const token = authManager.getAccessToken();
    
    if (token) {
      try {
        // Appel API pour déconnexion (optionnel, pour blacklist le token)
        // BEST PRACTICE: Utiliser buildApiUrl() pour construire l'URL de manière sûre
        await fetch(buildApiUrl(API_ENDPOINTS.LOGOUT), {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ refresh: authManager.getRefreshToken() }),
        });
      } catch (error) {
        console.warn('⚠️ Erreur lors de la déconnexion backend:', error);
        // Continuer quand même avec la déconnexion locale
      }
    }

    authManager.clearAuth();
    console.log('✅ Déconnexion réussie');
    return { success: true };
  }

  async refreshToken(refreshToken: string): Promise<{ access: string; refresh: string }> {
    console.log('🔄 Auth Service - Rafraîchissement du token');
    
    try {
      // BEST PRACTICE: Utiliser buildApiUrl() pour construire l'URL de manière sûre
      const response = await fetch(buildApiUrl(API_ENDPOINTS.REFRESH_TOKEN), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh: refreshToken }),
      });

      if (!response.ok) {
        throw new Error('Erreur de rafraîchissement du token');
      }

      const data = await response.json();
      const newTokens = {
        access: data.access,
        refresh: data.refresh || refreshToken // Garder l'ancien si pas de nouveau
      };

      authManager.setTokens(newTokens.access, newTokens.refresh);
      console.log('✅ Token rafraîchi avec succès');
      
      return newTokens;
    } catch (error) {
      console.error('❌ Erreur de rafraîchissement:', error);
      authManager.clearAuth();
      throw error;
    }
  }

  async getCurrentUser(): Promise<any> {
    console.log('👤 Auth Service - Récupération utilisateur actuel');
    
    const token = authManager.getAccessToken();
    
    if (!token) {
      console.warn('⚠️ Pas de token, utilisateur non connecté');
      return null;
    }

    try {
      // BEST PRACTICE: Utiliser buildApiUrl() pour construire l'URL de manière sûre
      const response = await fetch(buildApiUrl(API_ENDPOINTS.CURRENT_USER), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          // Token expiré, essayer de rafraîchir
          const refreshToken = authManager.getRefreshToken();
          if (refreshToken) {
            await this.refreshToken(refreshToken);
            return this.getCurrentUser(); // Réessayer avec le nouveau token
          }
        }
        throw new Error('Erreur de récupération utilisateur');
      }

      const data = await response.json();
      const user = this.normalizeUser(data.user || data);
      
      // Si les capabilities ne sont pas présentes, les charger depuis l'endpoint /permissions/me/
      if (!user.capabilities || user.capabilities.length === 0) {
        try {
          console.log('📋 Chargement des capabilities depuis /permissions/me/...');
          const { settingsService } = await import('./settings');
          const permissionsData = await settingsService.getMyPermissions();
          if (permissionsData?.capabilities) {
            user.capabilities = permissionsData.capabilities;
            console.log('✅ Capabilities chargées:', user.capabilities.length);
          }
        } catch (error) {
          console.warn('⚠️ Impossible de charger les capabilities, utilisation des données de getCurrentUser');
        }
      }
      
      authManager.setUser(user);
      console.log('✅ Utilisateur récupéré:', user.full_name);
      console.log('🔑 Rôle:', user.role);
      console.log('🔐 Capabilities:', user.capabilities?.length || 0);
      
      return user;
    } catch (error) {
      console.error('❌ Erreur récupération utilisateur:', error);
      
      // Ne pas utiliser de fallback sur l'utilisateur stocké localement
      // pour éviter d'utiliser des données obsolètes ou incorrectes
      // Si le backend ne répond pas, c'est une erreur réelle qu'il faut gérer
      throw error;
    }
  }

}

// Instance singleton
export const authService = new AuthService();
